import { z } from 'zod';
import { ERP_OPERATIONS, type ErpOperation } from './erp.js';

/**
 * DEFINIÇÃO DE CONECTOR — configurada no Master, executada pelo motor na instância.
 *
 * Descreve, sem código, como falar com a API de um ERP e como NORMALIZAR cada resposta
 * para o formato padrão da plataforma. É apenas dado: não há expressões nem scripts
 * avaliados. As credenciais nunca fazem parte da definição — ela só declara QUAIS campos
 * o provedor precisa informar (URL, usuário, token…).
 */

/* ───────────── autenticação e credenciais ───────────── */

export const ERP_AUTH_TYPES = ['none', 'basic', 'bearer', 'header', 'query', 'flow'] as const;
export type ErpAuthType = (typeof ERP_AUTH_TYPES)[number];

export const ERP_AUTH_LABELS: Record<ErpAuthType, string> = {
  none: 'Sem autenticação',
  basic: 'Basic (usuário e senha/token)',
  bearer: 'Bearer token',
  header: 'Token em cabeçalho',
  query: 'Token em parâmetro da URL',
  flow: 'Fluxo de autenticação (login, renovação de token…)',
};

const fieldKey = z.string().trim().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Use letras minúsculas, números e _');
const headerName = z.string().trim().regex(/^[A-Za-z0-9-]{1,64}$/, 'Nome de cabeçalho inválido');

export const credentialFieldSchema = z.object({
  key: fieldKey,
  label: z.string().trim().min(1).max(60),
  secret: z.boolean(),
  optional: z.boolean().optional(),
  hint: z.string().trim().max(200).default(''),
  /**
   * "url": endereço informado pelo provedor (ex.: servidor de autenticação em outro host, como pede o
   * Voalle em homologação). Etapas de autenticação podem chamar o host desse endereço.
   */
  kind: z.enum(['text', 'url']).default('text'),
});
export type CredentialField = z.infer<typeof credentialFieldSchema>;



/* ───────────── variáveis disponíveis nas requisições ───────────── */

/**
 * Valores que o Gateway de Segurança resolve no backend e injeta na requisição.
 * A IA nunca os fornece diretamente: são do cliente já identificado.
 */
/** Do cliente já identificado: disponíveis em todas as operações depois da identificação. */
const CUSTOMER_VARIABLES = [
  { key: 'cliente_id', description: 'ID do cliente no ERP' },
  { key: 'documento', description: 'CPF/CNPJ do cliente só com dígitos' },
  { key: 'documento_formatado', description: 'CPF/CNPJ do cliente com pontuação' },
];
const withCustomer = (own: { key: string; description: string }[]) => [...own, ...CUSTOMER_VARIABLES.filter((c) => !own.some((o) => o.key === c.key))];

export const OPERATION_VARIABLES: Record<ErpOperation, { key: string; description: string }[]> = {
  identificar_cliente: [
    { key: 'documento', description: 'CPF/CNPJ só com dígitos' },
    { key: 'documento_formatado', description: 'CPF/CNPJ com pontuação (000.000.000-00)' },
  ],
  consultar_contratos: withCustomer([]),
  consultar_faturas: withCustomer([{ key: 'contrato_id', description: 'ID do contrato no ERP' }]),
  obter_segunda_via: withCustomer([{ key: 'fatura_id', description: 'ID da fatura no ERP' }]),
  consultar_conexao: withCustomer([{ key: 'contrato_id', description: 'ID do contrato no ERP' }]),
  consultar_incidentes: withCustomer([{ key: 'regiao', description: 'Região/bairro do cliente (pode ser vazio)' }]),
  abrir_chamado: withCustomer([
    { key: 'contrato_id', description: 'ID do contrato no ERP' },
    { key: 'cliente_id', description: 'ID do cliente no ERP' },
    { key: 'motivo', description: 'Motivo confirmado pelo cliente' },
    { key: 'descricao', description: 'Descrição confirmada pelo cliente' },
  ]),
  consultar_chamados: withCustomer([{ key: 'contrato_id', description: 'ID do contrato no ERP' }]),
};

/** Variáveis comuns a todas as operações. */
export const COMMON_VARIABLES = [
  { key: 'hoje', description: 'Data de hoje (AAAA-MM-DD)' },
] as const;

/* ───────────── campos padrão (resultado normalizado) ───────────── */

export const FIELD_TRANSFORMS = ['text', 'digits', 'integer', 'decimal', 'cents', 'cents_from_decimal', 'date', 'datetime', 'boolean', 'phones'] as const;
export type FieldTransform = (typeof FIELD_TRANSFORMS)[number];

export const FIELD_TRANSFORM_LABELS: Record<FieldTransform, string> = {
  text: 'Texto',
  digits: 'Somente dígitos',
  integer: 'Número inteiro',
  decimal: 'Número decimal',
  cents: 'Valor já em centavos',
  cents_from_decimal: 'Valor em reais → centavos',
  date: 'Data (AAAA-MM-DD)',
  datetime: 'Data e hora (ISO)',
  boolean: 'Sim/Não',
  phones: 'Lista de telefones',
};

export interface StandardField {
  key: string;
  label: string;
  required: boolean;
  /** Transformação sugerida. */
  transform: FieldTransform;
  /** Valores aceitos (o mapa de valores deve convertê-los). */
  values?: readonly string[];
}

export const OPERATION_RESULT: Record<ErpOperation, { many: boolean; fields: StandardField[] }> = {
  identificar_cliente: {
    many: false,
    fields: [
      { key: 'id', label: 'ID do cliente', required: true, transform: 'text' },
      { key: 'name', label: 'Nome', required: true, transform: 'text' },
      { key: 'document', label: 'CPF/CNPJ', required: true, transform: 'digits' },
      { key: 'phones', label: 'Telefones', required: false, transform: 'phones' },
      { key: 'region', label: 'Região/bairro', required: false, transform: 'text' },
    ],
  },
  consultar_contratos: {
    many: true,
    fields: [
      { key: 'id', label: 'ID do contrato', required: true, transform: 'text' },
      { key: 'plan', label: 'Plano', required: true, transform: 'text' },
      { key: 'status', label: 'Situação', required: true, transform: 'text', values: ['ativo', 'suspenso', 'cancelado'] },
      { key: 'neighborhood', label: 'Bairro', required: false, transform: 'text' },
    ],
  },
  consultar_faturas: {
    many: true,
    fields: [
      { key: 'id', label: 'ID da fatura', required: true, transform: 'text' },
      { key: 'dueDate', label: 'Vencimento', required: true, transform: 'date' },
      { key: 'amountCents', label: 'Valor (centavos)', required: true, transform: 'cents_from_decimal' },
      { key: 'status', label: 'Situação', required: true, transform: 'text', values: ['aberta', 'paga', 'vencida'] },
    ],
  },
  obter_segunda_via: {
    many: false,
    fields: [
      { key: 'digitableLine', label: 'Linha digitável', required: false, transform: 'text' },
      { key: 'pixCode', label: 'Pix copia e cola', required: false, transform: 'text' },
      { key: 'link', label: 'Link do boleto', required: false, transform: 'text' },
    ],
  },
  consultar_conexao: {
    many: false,
    fields: [
      { key: 'online', label: 'Conectado', required: true, transform: 'boolean' },
      { key: 'lastSeen', label: 'Última conexão', required: false, transform: 'datetime' },
      { key: 'signalDbm', label: 'Sinal óptico (dBm)', required: false, transform: 'decimal' },
    ],
  },
  consultar_incidentes: {
    many: true,
    fields: [
      { key: 'id', label: 'ID', required: true, transform: 'text' },
      { key: 'title', label: 'Título', required: true, transform: 'text' },
      { key: 'status', label: 'Situação', required: true, transform: 'text' },
      { key: 'eta', label: 'Previsão', required: false, transform: 'text' },
    ],
  },
  abrir_chamado: {
    many: false,
    fields: [{ key: 'protocol', label: 'Protocolo', required: true, transform: 'text' }],
  },
  consultar_chamados: {
    many: true,
    fields: [
      { key: 'protocol', label: 'Protocolo', required: true, transform: 'text' },
      { key: 'subject', label: 'Assunto', required: true, transform: 'text' },
      { key: 'status', label: 'Situação', required: true, transform: 'text' },
      { key: 'openedAt', label: 'Aberto em', required: true, transform: 'datetime' },
    ],
  },
};

/* ───────────── operação ───────────── */

/**
 * Caminho no JSON: "registros", "data.items", "a.0.b". Alternativas com "|" (primeiro não vazio).
 * Para telefones, vários caminhos separados por "," são reunidos numa lista.
 */
const jsonPath = z.string().trim().max(300).regex(/^[A-Za-z0-9_.$|,\s-]*$/, 'Caminho inválido');

export const fieldMappingSchema = z.object({
  path: jsonPath,
  transform: z.enum(FIELD_TRANSFORMS),
  /** Conversão de valores do ERP para o padrão, ex.: { "A": "ativo", "S": "suspenso" }. */
  valueMap: z.record(z.string().max(100), z.string().max(100)).default({}),
  /** Valor quando o ERP não traz o campo (após o mapa). */
  fallback: z.string().max(100).optional(),
});
export type FieldMapping = z.infer<typeof fieldMappingSchema>;

const templateText = z.string().max(2000);

export const operationDefinitionSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT']),
  /** Sempre relativo à URL base do provedor; aceita {{variavel}}. */
  path: z
    .string()
    .trim()
    .max(500)
    .regex(/^\/(?!\/)[^\s]*$/, 'Comece com "/" (caminho relativo à URL base)'),
  query: z.record(z.string().max(100), templateText).default({}),
  headers: z.record(headerName, templateText).default({}),
  /** Formato do corpo: JSON (campo body) ou formulário x-www-form-urlencoded (campo form). */
  bodyFormat: z.enum(['json', 'form']).default('json'),
  /** Corpo JSON com {{variavel}} (valores inseridos já escapados). */
  body: templateText.nullable().default(null),
  /** Corpo em formulário (bodyFormat = form). */
  form: z.record(z.string().max(100), templateText).default({}),
  /** Onde está o resultado na resposta ("" = raiz). */
  resultPath: jsonPath.default(''),
  /** A resposta traz uma lista (para operações de item único, usa o primeiro). */
  many: z.boolean().default(false),
  fields: z.record(z.string(), fieldMappingSchema).default({}),
  /**
   * Chaves com valor FIXO e PÚBLICO documentado pelo fabricante (ex.: client_id "synauth" do refresh do
   * Voalle). Ficam fora da verificação de credenciais digitadas.
   */
  fixed: z.array(z.string().max(100)).max(30).default([]),
});
export type OperationDefinition = z.infer<typeof operationDefinitionSchema>;

/* ───────────── autenticação ───────────── */

export const AUTH_STEP_ROLES = ['login', 'refresh'] as const;
export type AuthStepRole = (typeof AUTH_STEP_ROLES)[number];
export const AUTH_STEP_ROLE_LABELS: Record<AuthStepRole, string> = {
  login: 'Login (obter acesso)',
  refresh: 'Renovação (refresh token)',
};

/**
 * Uma chamada do fluxo de autenticação. Extrai valores da resposta (ex.: access_token,
 * refresh_token, expires_in) que viram VARIÁVEIS DE SESSÃO usáveis nas demais chamadas
 * como {{access_token}}. Valores de sessão nunca saem da instância.
 */
export const authStepSchema = z.object({
  key: fieldKey,
  name: z.string().trim().min(1).max(60),
  role: z.enum(AUTH_STEP_ROLES),
  method: z.enum(['GET', 'POST', 'PUT']),
  /**
   * Relativa à URL base ("/connect/token") ou absoluta no MESMO host da URL base, com {{host}}
   * ("https://{{host}}:45700/connect/token"). A porta pode mudar; o host, não.
   */
  url: z.string().trim().min(1).max(500),
  query: z.record(z.string().max(100), templateText).default({}),
  headers: z.record(headerName, templateText).default({}),
  bodyFormat: z.enum(['json', 'form']).default('form'),
  body: templateText.nullable().default(null),
  form: z.record(z.string().max(100), templateText).default({}),
  /** Variável de sessão ← caminho na resposta. */
  outputs: z.record(fieldKey, jsonPath).default({}),
  /**
   * Chaves com valor FIXO e PÚBLICO documentado pelo fabricante (ex.: client_id "synauth" do refresh do
   * Voalle). Ficam fora da verificação de credenciais digitadas.
   */
  fixed: z.array(z.string().max(100)).max(30).default([]),
});
export type AuthStep = z.infer<typeof authStepSchema>;

export const erpAuthSchema = z.object({
  type: z.enum(ERP_AUTH_TYPES),
  /** basic: usuário; header/query/bearer: não usado */
  usernameField: fieldKey.optional(),
  /** basic: senha; bearer/header/query: token */
  secretField: fieldKey.optional(),
  /** header: nome do cabeçalho; query: nome do parâmetro */
  name: z.string().trim().max(64).optional(),
  /** flow: etapas, executadas em ordem dentro de cada papel (login, renovação). */
  steps: z.array(authStepSchema).max(8).default([]),
  /** flow: variável de sessão com a validade em segundos (ex.: expires_in). Sem ela, vale até o ERP responder 401. */
  expiresInVar: fieldKey.optional(),
});
export type ErpAuth = z.infer<typeof erpAuthSchema>;

/** Variáveis de sessão produzidas pelas etapas de um papel. */
export function sessionVariables(auth: ErpAuth, role?: AuthStepRole): string[] {
  return [...new Set(auth.type === 'flow' ? auth.steps.filter((s) => !role || s.role === role).flatMap((s) => Object.keys(s.outputs)) : [])];
}

/**
 * Converte formatos antigos para o atual antes de validar (dados já salvos nunca quebram):
 * - autenticação "oauth2_client_credentials" (versão anterior) vira um fluxo com a etapa "Login".
 */
export function upgradeLegacyDefinition(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const def = raw as Record<string, unknown>;
  const auth = def.auth as Record<string, unknown> | undefined;
  if (auth?.type !== 'oauth2_client_credentials') return raw;
  const accessPath = typeof auth.accessTokenPath === 'string' && auth.accessTokenPath ? auth.accessTokenPath : 'access_token';
  const expiresPath = typeof auth.expiresInPath === 'string' && auth.expiresInPath ? auth.expiresInPath : 'expires_in';
  const headers = { ...((def.headers as Record<string, string> | undefined) ?? {}) };
  if (!Object.keys(headers).some((h) => h.toLowerCase() === 'authorization')) headers.Authorization = 'Bearer {{access_token}}';
  return {
    ...def,
    headers,
    auth: {
      type: 'flow',
      expiresInVar: 'expires_in',
      steps: [
        {
          key: 'login',
          name: 'Login',
          role: 'login',
          method: 'POST',
          url: typeof auth.tokenUrl === 'string' && auth.tokenUrl ? auth.tokenUrl : 'https://{{host}}/',
          bodyFormat: 'form',
          form: (auth.tokenForm as Record<string, string> | undefined) ?? {},
          outputs: { access_token: accessPath, expires_in: expiresPath },
        },
      ],
    },
  };
}

const definitionObjectSchema = z.object({
  auth: erpAuthSchema.default({ type: 'none' }),
  /** A URL base é sempre pedida ao provedor; estes são os campos adicionais. */
  credentialFields: z.array(credentialFieldSchema).max(10).default([]),
  /** Cabeçalhos fixos enviados em toda chamada (sem segredos). */
  headers: z.record(headerName, z.string().max(500)).default({}),
  operations: z.object(Object.fromEntries(ERP_OPERATIONS.map((op) => [op, operationDefinitionSchema.optional()])) as Record<ErpOperation, z.ZodOptional<typeof operationDefinitionSchema>>).default({}),
});
export const erpConnectorDefinitionSchema = z.preprocess(upgradeLegacyDefinition, definitionObjectSchema);
export type ErpConnectorDefinition = z.infer<typeof definitionObjectSchema>;

/** Lê uma definição salva de forma tolerante: converte formatos antigos e completa padrões. */
export function readDefinition(raw: unknown): ErpConnectorDefinition {
  const r = erpConnectorDefinitionSchema.safeParse(raw ?? {});
  return r.success ? r.data : EMPTY_DEFINITION;
}

export const EMPTY_DEFINITION: ErpConnectorDefinition = {
  auth: { type: 'none', steps: [] },
  credentialFields: [],
  headers: {},
  operations: {},
};

/** Problemas que impedem a operação de funcionar (usados na homologação e na tela). */
export function definitionIssues(def: ErpConnectorDefinition, op: ErpOperation): string[] {
  const d = def.operations[op];
  if (!d) return ['Requisição não configurada'];
  const issues: string[] = [];
  const vars = new Set([
    ...OPERATION_VARIABLES[op].map((v) => v.key),
    ...COMMON_VARIABLES.map((v) => v.key),
    ...def.credentialFields.map((f) => f.key),
    ...sessionVariables(def.auth),
  ]);
  const texts = [d.path, ...Object.values(d.query), ...Object.values(d.headers), ...Object.values(def.headers), ...(d.bodyFormat === 'form' ? Object.values(d.form) : [d.body ?? ''])];
  for (const t of texts) {
    for (const m of t.matchAll(/\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/g)) {
      if (!vars.has(m[1]!)) issues.push(`Variável desconhecida: {{${m[1]}}}`);
    }
  }
  if (d.bodyFormat === 'json' && d.body && !jsonTemplateIsValid(d.body)) issues.push('Corpo não é um JSON válido');
  for (const f of OPERATION_RESULT[op].fields) {
    const m = d.fields[f.key];
    if (f.required && (!m || (!m.path && m.fallback === undefined))) issues.push(`Campo obrigatório sem mapeamento: ${f.label}`);
  }
  issues.push(...authIssues(def));
  if (def.auth.type !== 'flow' && def.auth.type !== 'none' && !def.auth.secretField) issues.push('Autenticação sem campo de token/senha');
  if (def.auth.type === 'basic' && !def.auth.usernameField) issues.push('Autenticação Basic sem campo de usuário');
  if ((def.auth.type === 'header' || def.auth.type === 'query') && !def.auth.name) issues.push('Informe o nome do cabeçalho/parâmetro do token');
  for (const k of [def.auth.usernameField, def.auth.secretField]) {
    if (k && !def.credentialFields.some((f) => f.key === k)) issues.push(`Campo de credencial inexistente: ${k}`);
  }
  return [...new Set(issues)];
}

const VAR_RE = /\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/g;

/** Problemas do fluxo de autenticação (vazio quando não é fluxo). */
export function authIssues(def: ErpConnectorDefinition): string[] {
  const a = def.auth;
  if (a.type !== 'flow') return [];
  const issues: string[] = [];
  const logins = a.steps.filter((s) => s.role === 'login');
  if (!logins.length) issues.push('Fluxo de autenticação sem etapa de login');
  const creds = def.credentialFields.map((f) => f.key);
  const seenKeys = new Set<string>();
  const available = new Set<string>([...creds, 'host', 'hoje']);
  const loginOutputs = sessionVariables(a, 'login');
  for (const role of AUTH_STEP_ROLES) {
    // renovação enxerga tudo o que o login produziu (ex.: {{refresh_token}})
    const scope = new Set<string>([...available, ...(role === 'refresh' ? loginOutputs : [])]);
    for (const step of a.steps.filter((s) => s.role === role)) {
      if (seenKeys.has(step.key)) issues.push(`Etapa "${step.name}": identificador repetido`);
      seenKeys.add(step.key);
      const urlFields = def.credentialFields.filter((f) => f.kind === 'url').map((f) => f.key);
      const viaUrlField = urlFields.some((k) => new RegExp(`^\\{\\{\\s*${k}\\s*\\}\\}`).test(step.url));
      if (!step.url.startsWith('/') && !/^https:\/\/\{\{\s*host\s*\}\}([:/]|$)/.test(step.url) && !viaUrlField) {
        issues.push(`Etapa "${step.name}": use um caminho relativo (/...), https://{{host}}/... ou um campo de credencial do tipo URL (ex.: {{url_autenticacao}}/connect/token)`);
      }
      const texts = [step.url, ...Object.values(step.query), ...Object.values(step.headers), ...(step.bodyFormat === 'form' ? Object.values(step.form) : [step.body ?? ''])];
      for (const t of texts) for (const m of t.matchAll(VAR_RE)) if (!scope.has(m[1]!)) issues.push(`Etapa "${step.name}": variável desconhecida {{${m[1]}}}`);
      if (!Object.keys(step.outputs).length) issues.push(`Etapa "${step.name}": nenhum valor extraído da resposta`);
      for (const k of Object.keys(step.outputs)) {
        if (creds.includes(k)) issues.push(`Etapa "${step.name}": a variável ${k} tem o mesmo nome de um campo de credencial`);
        scope.add(k);
      }
    }
  }
  if (a.expiresInVar && !sessionVariables(a).includes(a.expiresInVar)) issues.push(`Validade: nenhuma etapa extrai "${a.expiresInVar}"`);
  return [...new Set(issues)];
}

/* ───────────── credenciais digitadas na definição ───────────── */

/** Chaves cujo valor é credencial: precisam ser {{campo}}, nunca o valor digitado. */
const CREDENTIAL_KEY = /(secret|senha|password|passwd|token|syndata|api[_-]?key|apikey|chave|signature|client[_-]?id|username|usuario|^authorization)$/i;

const snakeKey = (k: string) =>
  k
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'c_$1')
    .slice(0, 40) || 'campo';

/** Valor com conteúdo literal além de {{variaveis}} (ex.: o segredo em si). */
function isLiteral(key: string, value: string) {
  let rest = value.replace(VAR_RE, '');
  // "Bearer {{access_token}}" é permitido; "Bearer eyJ..." não.
  if (/^authorization$/i.test(key)) rest = rest.replace(/^\s*(bearer|basic|token)\b/i, '');
  return rest.replace(/\s+/g, '').length >= 4;
}

export type LiteralTarget = { kind: 'headers' } | { kind: 'step'; key: string } | { kind: 'operation'; op: ErpOperation };

export interface LiteralHit {
  /** Onde está, para exibir (sem o valor). */
  where: string;
  key: string;
  target: LiteralTarget;
}

type Visitor = (where: string, key: string, value: string, target: LiteralTarget, fixed: string[]) => string;

/** Percorre todos os textos que vão nas requisições, permitindo substituir valores. */
function visitDefinition(def: ErpConnectorDefinition, visit: Visitor): ErpConnectorDefinition {
  const rec = (where: string, r: Record<string, string>, target: LiteralTarget, fixed: string[] = []) =>
    Object.fromEntries(Object.entries(r).map(([k, v]) => [k, visit(where, k, v, target, fixed)]));
  const json = (where: string, body: string | null, target: LiteralTarget, fixed: string[]) => {
    if (!body) return body;
    let data: unknown;
    try {
      data = JSON.parse(body.replace(/"\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}"/g, '"{{$1}}"'));
    } catch {
      return body;
    }
    let changed = false;
    const walk = (v: unknown, key: string): unknown => {
      if (Array.isArray(v)) return v.map((x) => walk(x, key));
      if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x, k)]));
      if (typeof v === 'string' || typeof v === 'number') {
        const next = visit(where, key, String(v), target, fixed);
        if (next !== String(v)) {
          changed = true;
          return next;
        }
      }
      return v;
    };
    const out = walk(data, '');
    return changed ? JSON.stringify(out, null, 2) : body;
  };
  return {
    ...def,
    headers: rec('Cabeçalhos fixos', def.headers, { kind: 'headers' }),
    auth: {
      ...def.auth,
      steps: def.auth.steps.map((s) => ({
        ...s,
        query: rec(`Etapa "${s.name}"`, s.query, { kind: 'step', key: s.key }, s.fixed),
        headers: rec(`Etapa "${s.name}"`, s.headers, { kind: 'step', key: s.key }, s.fixed),
        form: rec(`Etapa "${s.name}"`, s.form, { kind: 'step', key: s.key }, s.fixed),
        body: json(`Etapa "${s.name}"`, s.body, { kind: 'step', key: s.key }, s.fixed),
      })),
    },
    operations: Object.fromEntries(
      Object.entries(def.operations).map(([op, d]) => [
        op,
        d && {
          ...d,
          query: rec(`Operação ${op}`, d.query, { kind: 'operation', op: op as ErpOperation }, d.fixed),
          headers: rec(`Operação ${op}`, d.headers, { kind: 'operation', op: op as ErpOperation }, d.fixed),
          form: rec(`Operação ${op}`, d.form, { kind: 'operation', op: op as ErpOperation }, d.fixed),
          body: json(`Operação ${op}`, d.body, { kind: 'operation', op: op as ErpOperation }, d.fixed),
        },
      ]),
    ) as ErpConnectorDefinition['operations'],
  };
}

/**
 * Credenciais digitadas direto na definição. A definição é gravada no Master, exportada e enviada a
 * TODAS as instâncias que usam o conector: credenciais precisam ser {{campo}}, preenchidas por provedor.
 */
export function literalCredentials(def: ErpConnectorDefinition): LiteralHit[] {
  const hits: LiteralHit[] = [];
  visitDefinition(def, (where, key, value, target, fixed) => {
    if (CREDENTIAL_KEY.test(key) && isLiteral(key, value) && !fixed.includes(key)) hits.push({ where, key, target });
    return value;
  });
  return hits;
}

/** Troca credenciais digitadas por {{campo}} e declara os campos. Os valores digitados são descartados. */
export function extractLiteralCredentials(def: ErpConnectorDefinition): { definition: ErpConnectorDefinition; fields: CredentialField[] } {
  const fields = new Map(def.credentialFields.map((f) => [f.key, f]));
  const added: CredentialField[] = [];
  const next = visitDefinition(def, (_where, key, value, _target, fixed) => {
    if (!CREDENTIAL_KEY.test(key) || !isLiteral(key, value) || fixed.includes(key)) return value;
    const k = snakeKey(key);
    if (/^authorization$/i.test(key)) {
      if (!fields.has('authorization')) {
        const f: CredentialField = { key: 'authorization', label: 'Authorization', secret: true, hint: '', kind: 'text' };
        fields.set('authorization', f);
        added.push(f);
      }
      const scheme = value.match(/^\s*(bearer|basic|token)\s+/i)?.[0] ?? '';
      return `${scheme}{{authorization}}`;
    }
    if (!fields.has(k)) {
      const f: CredentialField = { key: k, label: key.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), secret: !/(client[_-]?id|username|usuario)$/i.test(key), hint: '', kind: 'text' };
      fields.set(k, f);
      added.push(f);
    }
    return `{{${k}}}`;
  });
  return { definition: { ...next, credentialFields: [...def.credentialFields, ...added] }, fields: added };
}

/** Marca uma chave como valor fixo público do fabricante no ponto indicado. */
export function markFixed(def: ErpConnectorDefinition, target: LiteralTarget, key: string): ErpConnectorDefinition {
  const add = (list: string[]) => (list.includes(key) ? list : [...list, key]);
  if (target.kind === 'step') {
    return { ...def, auth: { ...def.auth, steps: def.auth.steps.map((s) => (s.key === target.key ? { ...s, fixed: add(s.fixed) } : s)) } };
  }
  if (target.kind === 'operation') {
    const d = def.operations[target.op];
    return d ? { ...def, operations: { ...def.operations, [target.op]: { ...d, fixed: add(d.fixed) } } } : def;
  }
  return def;
}

/** Valida um corpo JSON com {{variaveis}}: dentro de aspas valem como texto; fora, como valor. */
export function jsonTemplateIsValid(template: string): boolean {
  let out = '';
  let inString = false;
  for (let i = 0; i < template.length; i++) {
    const ch = template[i]!;
    if (ch === '{' && template[i + 1] === '{') {
      const m = template.slice(i).match(/^\{\{\s*[a-z_][a-z0-9_]*\s*\}\}/);
      if (m) {
        out += inString ? 'x' : '0';
        i += m[0].length - 1;
        continue;
      }
    }
    if (ch === '"' && template[i - 1] !== '\\') inString = !inString;
    out += ch;
  }
  try {
    JSON.parse(out);
    return true;
  } catch {
    return false;
  }
}
